import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { CONFIG, FAUCET_ABI } from './config';

/* global PentagonWidget */

const PG_AUTH_BASE = '/api/auth';
const PG_CLIENT_ID = 'we9dvc146c3dRFSS248d';
const PG_SSO_REDIRECT = 'https://faucet.pentagon.games'; // Must match SSO client registration at PG

function setCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; secure; samesite=strict`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function removeCookie(name) {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch { return null; }
}

// LoginModal as a stable component (not recreated on parent re-renders)
function LoginModal({ username, setUsername, password, setPassword, loginError, loggingIn, handleLogin, handleLoginKeyDown }) {
  return (
    <div className="modal-overlay">
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>PenDeFi Paper Trading</h2>
          <p>Sign in with your Pentagon Games account to claim tokens and compete.</p>
        </div>

        <div className="login-form">
          <input
            type="text"
            placeholder="Username or email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleLoginKeyDown}
            autoComplete="username"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleLoginKeyDown}
            autoComplete="current-password"
          />
          {loginError && <p className="login-error">{loginError}</p>}
          <button
            className="login-btn"
            onClick={handleLogin}
            disabled={loggingIn}
          >
            {loggingIn ? 'Signing in...' : 'Sign In'}
          </button>
          <p className="signup-link">
            Don't have an account?{' '}
            <a href="https://pentagon.games/sign-up" target="_blank" rel="noopener noreferrer">
              Sign up
            </a>
          </p>
        </div>

        <hr className="modal-divider" />

        <div className="modal-links">
          <p className="links-label">Community</p>
          <div className="links-grid">
            {[
              { label: '🐦 X / Twitter', url: 'https://x.com/PentagonGamesXP' },
              { label: '💬 Discord', url: 'https://discord.gg/pentagongamesxp' },
              { label: '✈️ Telegram', url: 'https://t.me/pentagongamesxp' },
              { label: '🌐 Pentagon Games', url: 'https://pentagon.games' },
            ].map((l) => (
              <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" className="link-card">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [walletAddress, setWalletAddress] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);

  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Faucet state
  const [hasClaimed, setHasClaimed] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [loading, setLoading] = useState(true);
  const [walletConnected, setWalletConnected] = useState(false);

  // Stats
  const [totalClaimants, setTotalClaimants] = useState(0);
  const [btcAmount, setBtcAmount] = useState('0.5');
  const [usdcAmount, setUsdcAmount] = useState('50,000');

  // Error
  const [error, setError] = useState('');

  const logoutTimerRef = useRef(null);

  // Load faucet read-only data
  const loadFaucetData = useCallback(async (address) => {
    setLoading(true);
    try {
      const rpcProvider = new ethers.JsonRpcProvider(CONFIG.RPC_URL);
      const contract = new ethers.Contract(CONFIG.FAUCET_ADDRESS, FAUCET_ABI, rpcProvider);

      const [claimed, paused, count, btcRaw, usdcRaw] = await Promise.all([
        address ? contract.hasClaimed(address) : false,
        contract.paused(),
        contract.totalClaimants(),
        contract.btcAmount(),
        contract.usdcAmount(),
      ]);

      setHasClaimed(claimed);
      setIsPaused(paused);
      setTotalClaimants(Number(count));
      setBtcAmount((Number(btcRaw) / 1e8).toString());
      setUsdcAmount(Number(Number(usdcRaw) / 1e6).toLocaleString());
    } catch (err) {
      console.error('Failed to load faucet data:', err);
    }
    setLoading(false);
  }, []);

  // Handle successful token login
  const handleToken = useCallback((token) => {
    const decoded = decodeJwt(token);
    if (!decoded) return;

    const address = decoded.mm_address;
    const expiry = decoded.exp * 1000;

    setCookie('auth_token', token);
    setCookie('token_expiration', expiry.toString());
    setCookie('wallet_address', address);

    setIsLoggedIn(true);
    setWalletAddress(address);
    loadFaucetData(address);

    // Auto-logout on expiry
    const timeLeft = expiry - Date.now();
    if (timeLeft > 0) {
      logoutTimerRef.current = setTimeout(() => handleLogout(), timeLeft);
    } else {
      handleLogout();
    }
  }, [loadFaucetData]);

  // Logout
  const handleLogout = () => {
    setIsLoggedIn(false);
    setWalletAddress('');
    setHasClaimed(false);
    setTxHash('');
    setWalletConnected(false);
    setProvider(null);
    setSigner(null);
    removeCookie('auth_token');
    removeCookie('token_expiration');
    removeCookie('wallet_address');
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    window.location.reload();
  };

  // Pentagon login form submit
  const handleLogin = async () => {
    if (!username || !password) {
      setLoginError('Enter your username/email and password');
      return;
    }
    setLoggingIn(true);
    setLoginError('');

    try {
      // Step 1: Login
      const loginRes = await fetch(`${PG_AUTH_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, type: 'email' }),
      });
      const loginData = await loginRes.json();

      if (!loginData.status) {
        setLoginError(loginData.message || 'Login failed');
        setLoggingIn(false);
        return;
      }

      const accessToken = loginData.result?.access_token;
      if (!accessToken) {
        setLoginError('Login succeeded but no token received');
        setLoggingIn(false);
        return;
      }

      // Step 2: SSO authorize
      const authRes = await fetch(`${PG_AUTH_BASE}/sso-authorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: PG_CLIENT_ID,
          redirect_uri: PG_SSO_REDIRECT,
        }),
      });
      const authData = await authRes.json();

      if (!authData.status) {
        setLoginError('SSO authorization failed');
        setLoggingIn(false);
        return;
      }

      // Step 3: Token exchange
      const tokenRes = await fetch(`${PG_AUTH_BASE}/sso-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorization_code: authData.token,
          access_token: accessToken,
        }),
      });
      const tokenData = await tokenRes.json();

      if (tokenData.status && tokenData.token) {
        handleToken(tokenData.token);
      } else {
        setLoginError('Token exchange failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Connection error. Please try again.');
    }
    setLoggingIn(false);
  };

  const handleLoginKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  // Check for existing session on mount
  useEffect(() => {
    // Check URL token param (from widget redirect)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      handleToken(urlToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Check cookies
    const token = getCookie('auth_token');
    const expiry = getCookie('token_expiration');
    const addr = getCookie('wallet_address');
    if (token && expiry) {
      const exp = parseInt(expiry, 10);
      if (Date.now() < exp) {
        setIsLoggedIn(true);
        setWalletAddress(addr);
        loadFaucetData(addr);
        const timeLeft = exp - Date.now();
        logoutTimerRef.current = setTimeout(() => handleLogout(), timeLeft);
      } else {
        handleLogout();
      }
    } else {
      loadFaucetData(null);
    }
  }, [handleToken, loadFaucetData]);

  // Mount Pentagon Widget + listen for SSO event
  useEffect(() => {
    if (typeof PentagonWidget !== 'undefined' && PentagonWidget.mount) {
      PentagonWidget.mount();
    }

    const onWidgetLogin = (e) => {
      if (e?.detail?.token) handleToken(e.detail.token);
    };

    window.addEventListener('PentagonGamesSignInOnSuccess', onWidgetLogin);
    return () => {
      window.removeEventListener('PentagonGamesSignInOnSuccess', onWidgetLogin);
    };
  }, [handleToken]);

  // Connect MetaMask wallet for claim transaction
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('MetaMask or compatible wallet required to claim tokens.');
      return;
    }
    try {
      const browserProvider = new ethers.BrowserProvider(window.ethereum);

      // Switch to correct chain
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x' + CONFIG.CHAIN_ID.toString(16) }],
        });
      } catch (switchErr) {
        if (switchErr.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: '0x' + CONFIG.CHAIN_ID.toString(16),
              chainName: CONFIG.CHAIN_NAME,
              rpcUrls: [CONFIG.RPC_URL],
              blockExplorerUrls: [CONFIG.EXPLORER_URL],
            }],
          });
        }
      }

      const accounts = await browserProvider.send('eth_requestAccounts', []);
      const s = await browserProvider.getSigner();
      const connectedAddr = accounts[0];
      setProvider(browserProvider);
      setSigner(s);
      setWalletConnected(true);
      setWalletAddress(connectedAddr);

      // Re-check claim status for the connected wallet
      loadFaucetData(connectedAddr);
    } catch (err) {
      console.error('Wallet connect error:', err);
      setError('Failed to connect wallet.');
    }
  };

  // Claim tokens
  const handleClaim = async () => {
    if (claiming || hasClaimed || isPaused || !signer) return;
    setClaiming(true);
    setTxHash('');
    setError('');

    try {
      const contract = new ethers.Contract(CONFIG.FAUCET_ADDRESS, FAUCET_ABI, signer);
      const tx = await contract.claim();
      const receipt = await tx.wait();
      setTxHash(receipt.hash);
      setHasClaimed(true);
      setTotalClaimants(prev => prev + 1);
    } catch (err) {
      console.error('Claim failed:', err);
      const msg = err?.reason || err?.message || 'Claim failed. Please try again.';
      if (msg.includes('Already claimed')) {
        setHasClaimed(true);
        setError('You have already claimed tokens.');
      } else if (msg.includes('paused')) {
        setIsPaused(true);
        setError('The faucet is currently paused.');
      } else {
        setError(msg.length > 120 ? msg.substring(0, 120) + '...' : msg);
      }
    }
    setClaiming(false);
  };

  const shortenAddress = (addr) => {
    if (!addr) return '';
    return addr.slice(0, 6) + '...' + addr.slice(-4);
  };



  return (
    <div className="app" id="dark">
      {/* Header */}
      <header className="faucet-header">
        <div className="header-left">
          <a href={CONFIG.COMPETITION_URL} className="logo-link">
            <span className="logo-text">PenDeFi</span>
          </a>
          <span className="testnet-badge">Testnet</span>
          <nav className="header-nav">
            <a href={CONFIG.EXCHANGE_URL}>Competition Trade</a>
            <a href="/" className="nav-active">Faucet</a>
            <a href={CONFIG.LEADERBOARD_URL}>Leaderboard</a>
          </nav>
        </div>
        <div className="header-right">
          {isLoggedIn && walletAddress && (
            <>
              <span className="wallet-chip">{shortenAddress(walletAddress)}</span>
              <button className="logout-btn" onClick={handleLogout}>Sign Out</button>
            </>
          )}
        </div>
      </header>

      {/* Login modal overlay when not logged in */}
      {!isLoggedIn && (
        <LoginModal
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          loginError={loginError}
          loggingIn={loggingIn}
          handleLogin={handleLogin}
          handleLoginKeyDown={handleLoginKeyDown}
        />
      )}

      {/* Main content */}
      <main className="container">
        <div className="box">
          <div className="box-content">
            <h1 className="card-title">Paper Trading Competition</h1>
            <p className="card-subtitle">
              Claim test tokens and compete on the PenDeFi exchange leaderboard
            </p>
            <span className="vip-tag">VIP Only</span>

            {/* Signed in status */}
            {isLoggedIn && walletAddress && (
              <div className="signed-in-bar">
                <div>
                  <span className="signed-label">Signed in as</span>
                  <p className="signed-address">{shortenAddress(walletAddress)}</p>
                </div>
              </div>
            )}

            {/* Token amounts */}
            <div className="token-grid">
              <div className="token-card">
                <span className="token-icon-btc">₿</span>
                <div>
                  <div className="token-label">Bitcoin</div>
                  <div className="token-value">{btcAmount} BTC</div>
                </div>
              </div>
              <div className="token-card">
                <span className="token-icon-usdc">$</span>
                <div>
                  <div className="token-label">USD Coin</div>
                  <div className="token-value">{usdcAmount} USDC</div>
                </div>
              </div>
            </div>

            <p className="desc-text">
              Get <strong>{btcAmount} BTC</strong> and <strong>{usdcAmount} USDC</strong> to paper
              trade on the PenDeFi exchange. Each wallet can claim once. Trade, climb the
              leaderboard, and compete for prizes.
            </p>

            {/* Stats */}
            <div className="stats-row">
              <div className="stat-cell">
                <span className="stat-num">{totalClaimants}</span>
                <span className="stat-label">Participants</span>
              </div>
              <div className="stat-cell">
                <span className="stat-num">Base Sepolia</span>
                <span className="stat-label">Network</span>
              </div>
              <div className="stat-cell">
                <span className="stat-num">1</span>
                <span className="stat-label">Claims per Wallet</span>
              </div>
            </div>

            {/* Action area */}
            <div className="action-area">
              {loading && <div className="spinner"></div>}

              {!loading && isLoggedIn && isPaused && !hasClaimed && (
                <div className="state-box state-paused">
                  <div className="state-icon">⏸</div>
                  <p>The faucet is temporarily paused. Check back soon.</p>
                </div>
              )}

              {!loading && isLoggedIn && hasClaimed && !txHash && (
                <div className="state-box state-claimed">
                  <div className="state-icon">✓</div>
                  <p>You've already claimed your tokens. Go trade!</p>
                  <a className="btn-secondary" href={CONFIG.EXCHANGE_URL} target="_blank" rel="noopener noreferrer">
                    Open Exchange
                  </a>
                </div>
              )}

              {!loading && isLoggedIn && !hasClaimed && !isPaused && !claiming && !txHash && (
                <div className="state-box state-ready">
                  {!walletConnected ? (
                    <>
                      <p>Connect your wallet to claim tokens.</p>
                      <button className="btn-claim" onClick={connectWallet}>
                        🔗 Connect Wallet & Claim
                      </button>
                    </>
                  ) : (
                    <button className="btn-claim" onClick={handleClaim}>
                      Claim Tokens
                    </button>
                  )}
                </div>
              )}

              {claiming && (
                <div className="state-box">
                  <div className="spinner"></div>
                  <p>Confirming transaction...</p>
                </div>
              )}

              {txHash && (
                <div className="state-box state-success">
                  <div className="state-icon">🎉</div>
                  <p>Tokens claimed successfully!</p>
                  <a
                    href={`${CONFIG.EXPLORER_URL}/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tx-link"
                  >
                    View transaction on BaseScan ↗
                  </a>
                  <div className="next-step-box">
                    <p className="next-step-title">Next: Get free gas to deposit</p>
                    <p className="next-step-desc">You need Base Sepolia ETH to deposit your tokens into the exchange.</p>
                    <div className="gas-faucet-links">
                      <a href="https://www.alchemy.com/faucets/base-sepolia" target="_blank" rel="noopener noreferrer" className="gas-link">Alchemy Faucet ↗</a>
                      <a href="https://faucet.quicknode.com/base/sepolia" target="_blank" rel="noopener noreferrer" className="gas-link">QuickNode Faucet ↗</a>
                    </div>
                  </div>
                  <a className="btn-secondary" href={CONFIG.EXCHANGE_URL} target="_blank" rel="noopener noreferrer">
                    Start Trading
                  </a>
                </div>
              )}

              {error && <div className="error-msg">{error}</div>}
            </div>
          </div>
        </div>

        {/* Rules */}
        <div className="box rules-box">
          <div className="box-content">
            <h2>Competition Rules</h2>

            <div className="rule">
              <div className="rule-num">1</div>
              <div>
                <h3>VIP Access Only</h3>
                <p>The paper trading faucet is exclusively available to VIP members. Sign in with your Pentagon Games account to participate.</p>
              </div>
            </div>

            <div className="rule">
              <div className="rule-num">2</div>
              <div>
                <h3>One Claim Per Wallet</h3>
                <p>Each wallet address can claim tokens exactly once. You'll receive {btcAmount} BTC and {usdcAmount} USDC to trade with.</p>
              </div>
            </div>

            <div className="rule">
              <div className="rule-num">3</div>
              <div>
                <h3>Trade Only on PenDeFi</h3>
                <p>Use your tokens exclusively on the PenDeFi exchange. All trades count toward your leaderboard ranking.</p>
              </div>
            </div>

            <div className="rule rule-danger">
              <div className="rule-num">!</div>
              <div>
                <h3>No External Transfers</h3>
                <p>
                  Do not send tokens to or receive tokens from other wallets. Accounts that
                  aggregate funds, transfer tokens, or receive from external wallets will be
                  <strong> permanently disqualified</strong> from the leaderboard.
                </p>
              </div>
            </div>

            <div className="rule">
              <div className="rule-num">5</div>
              <div>
                <h3>Leaderboard Rankings</h3>
                <p>Rankings are based on your portfolio balance. The highest total balance (BTC + USDC value) wins.</p>
              </div>
            </div>

            <div className="rule">
              <div className="rule-num">6</div>
              <div>
                <h3>Fair Play</h3>
                <p>Any attempt to manipulate the competition through wash trading, multi-accounting, or fund pooling will result in disqualification.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <footer className="faucet-footer">
          <a href={CONFIG.COMPETITION_URL}>Competition</a>
          <span className="dot">·</span>
          <a href={CONFIG.EXCHANGE_URL}>Competition Trade</a>
          <span className="dot">·</span>
          <a href={CONFIG.LEADERBOARD_URL}>Leaderboard</a>
          <span className="dot">·</span>
          <a href="https://pentagon.games" target="_blank" rel="noopener noreferrer">Pentagon Games</a>
        </footer>
      </main>
    </div>
  );
}

export default App;
